import { JSX, useMemo, useState } from "react";
import LoadingZone from "../loading-zone/LoadingZone";
import TabContainer from "../tab-container/TabContainer";
import { UICustomizationSettingsDialogTabEnum } from "./UICustomizationSettingsTabEnum";
import TabWithCount from "../tab-with-count/TabWithCount";
import TabBar from "../tab-bar/TabBar";
import { UICustomizationFieldType } from "./UICustomizationSettingsFieldTypes";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import UICustomizationViewCategorySettings from "./UICustomizationViewCategorySettings";

export type UICustomizationViewCategoriesSettingsProps = {
    isLoading: boolean;
};

export default function UICustomizationViewCategoriesSettings({
    isLoading
}: UICustomizationViewCategoriesSettingsProps): JSX.Element {

    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<UICustomizationSettingsDialogTabEnum>(UICustomizationSettingsDialogTabEnum.Group);
    const {
        getValues
    } = useFormContext<UICustomizationFieldType>();

    const categories = getValues("customizationSettings.navigationDrawer.viewCategories");

    const {
        groupPageCount,
        devicePageCount,
        wellPageCount,
        tankBatteryPageCount,
        tankPageCount,
        tagPageCount
    } = useMemo(() => {
        return {
            groupPageCount: categories?.group.views.filter(v => !v.hidden).length ?? 0,
            devicePageCount: categories?.device.views.filter(v => !v.hidden).length ?? 0,
            wellPageCount: categories?.well.views.filter(v => !v.hidden).length ?? 0,
            tankBatteryPageCount: categories?.tankBattery.views.filter(v => !v.hidden).length ?? 0,
            tankPageCount: categories?.tank.views.filter(v => !v.hidden).length ?? 0,
            tagPageCount: categories?.tag.views.filter(v => !v.hidden).length ?? 0
        };
    }, [
        categories?.group.views,
        categories?.device.views,
        categories?.well.views,
        categories?.tankBattery.views,
        categories?.tank.views,
        categories?.tag.views
    ]);

    return (
        <>
            <LoadingZone isLoading={isLoading}>
                <TabBar
                    value={activeTab}
                    onChange={(_event, value) => setActiveTab(value)}
                >
                    <TabWithCount label={t("uiCustomizationSettings.tabs.groups.title") as string}
                        count={groupPageCount}
                        countDisabled={activeTab !== UICustomizationSettingsDialogTabEnum.Group}
                        value={UICustomizationSettingsDialogTabEnum.Group} />

                    <TabWithCount label={t("uiCustomizationSettings.tabs.devices.title") as string}
                        count={devicePageCount}
                        countDisabled={activeTab !== UICustomizationSettingsDialogTabEnum.Device}
                        value={UICustomizationSettingsDialogTabEnum.Device} />

                    <TabWithCount label={t("uiCustomizationSettings.tabs.wells.title") as string}
                        count={wellPageCount}
                        countDisabled={activeTab !== UICustomizationSettingsDialogTabEnum.Well}
                        value={UICustomizationSettingsDialogTabEnum.Well} />

                    <TabWithCount label={t("uiCustomizationSettings.tabs.tankBatteries.title") as string}
                        count={tankBatteryPageCount}
                        countDisabled={activeTab !== UICustomizationSettingsDialogTabEnum.TankBattery}
                        value={UICustomizationSettingsDialogTabEnum.TankBattery} />

                    <TabWithCount label={t("uiCustomizationSettings.tabs.tanks.title") as string}
                        count={tankPageCount}
                        countDisabled={activeTab !== UICustomizationSettingsDialogTabEnum.Tank}
                        value={UICustomizationSettingsDialogTabEnum.Tank} />

                    <TabWithCount label={t("uiCustomizationSettings.tabs.tags.title") as string}
                        count={tagPageCount}
                        countDisabled={activeTab !== UICustomizationSettingsDialogTabEnum.Tag}
                        value={UICustomizationSettingsDialogTabEnum.Tag} />
                </TabBar>

                <TabContainer visible={activeTab === UICustomizationSettingsDialogTabEnum.Group}>
                    <UICustomizationViewCategorySettings
                        fieldKey="customizationSettings.navigationDrawer.viewCategories.group"
                    />
                </TabContainer>

                <TabContainer visible={activeTab === UICustomizationSettingsDialogTabEnum.Device}>
                    <UICustomizationViewCategorySettings
                        fieldKey="customizationSettings.navigationDrawer.viewCategories.device"
                    />
                </TabContainer>

                <TabContainer visible={activeTab === UICustomizationSettingsDialogTabEnum.Well}>
                    <UICustomizationViewCategorySettings
                        fieldKey="customizationSettings.navigationDrawer.viewCategories.well"
                    />
                </TabContainer>

                <TabContainer visible={activeTab === UICustomizationSettingsDialogTabEnum.TankBattery}>
                    <UICustomizationViewCategorySettings
                        fieldKey="customizationSettings.navigationDrawer.viewCategories.tankBattery"
                    />
                </TabContainer>

                <TabContainer visible={activeTab === UICustomizationSettingsDialogTabEnum.Tank}>
                    <UICustomizationViewCategorySettings
                        fieldKey="customizationSettings.navigationDrawer.viewCategories.tank"
                    />
                </TabContainer>

                <TabContainer visible={activeTab === UICustomizationSettingsDialogTabEnum.Tag}>
                    <UICustomizationViewCategorySettings
                        fieldKey="customizationSettings.navigationDrawer.viewCategories.tag"
                    />
                </TabContainer>
            </LoadingZone>
        </>
    );
}